import { useTranslation } from "react-i18next";
import { getSportKey } from "../../workoutStatus";

/**
 * SessionHero — Today screen, hero card for "сегодняшняя сессия".
 *
 * Pure presentational atom: takes the planned workout brief + optional
 * structure intervals and paints a compact card with sport dot, scheduled
 * time of day, headline, 3 metric tiles (volume / pace / HR target), and
 * optional interval breakdown + CTAs (Start / Edit).
 *
 * Numbers come from the caller — no formatting magic here beyond
 * minute→hh:mm pretty print, and km→formatted distance with one decimal.
 *
 * The design source (Storica Desktop - Today.dc.html section heroSession) uses
 * Onest / JetBrains Mono tabular-nums for the metric tiles; we replicate
 * the chip pattern via tailwind tokens (bg-bg-surface / border-bg-border)
 * so light theme is automatic.
 */

export type SessionTimeOfDay = "morning" | "midday" | "evening" | null;

export interface SessionInterval {
  label: string;
  detail: string;
  /** Optional zone hint (z1..z7) — used to paint the strip color. */
  zone?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
}

export interface SessionHeroProps {
  discipline: string | null;
  title: string | null;
  /** Scheduled local time "HH:MM" — when null, time row is hidden. */
  scheduledTime?: string | null;
  timeOfDay?: SessionTimeOfDay;
  /** Planned duration in minutes. Renders as "70 мин" or "1h 10m". */
  durationMinutes?: number | null;
  /** Planned distance in km. Renders with one decimal when < 10, else 0. */
  distanceKm?: number | null;
  /** Target HR range, already formatted (e.g. "138-148"). */
  hrTarget?: string | null;
  /** Optional pace target, already formatted (e.g. "5:50"). */
  paceTarget?: string | null;
  intervals?: SessionInterval[];
  onStart?: () => void;
  onEdit?: () => void;
  /** Empty-state — no workout planned today. */
  empty?: boolean;
  className?: string;
}

// Семь зон → шкала мощности --zp*, а не пятиступенчатая семантическая
// --z1…--z5: раньше 1-5 брались оттуда, а 6/7 из hex-фолбэков, и ZonesMini
// рядом красил те же зоны совсем другой палитрой (зона 1: циан здесь,
// синий там). Обе таблицы сведены на --zp1…--zp7.
const ZONE_COLOR: Record<NonNullable<SessionInterval["zone"]>, string> = {
  1: "var(--zp1)",
  2: "var(--zp2)",
  3: "var(--zp3)",
  4: "var(--zp4)",
  5: "var(--zp5)",
  6: "var(--zp6)",
  7: "var(--zp7)",
};

const SPORT_DOT: Record<string, string> = {
  swim: "var(--disc-swim)",
  cycle: "var(--disc-bike)",
  gravel: "var(--disc-gravel)",
  run: "var(--disc-run)",
  trail: "var(--disc-trail)",
  triathlon: "var(--disc-triathlon)",
  duathlon: "var(--disc-duathlon)",
  hyrox: "var(--disc-hyrox)",
  strength: "var(--disc-strength)",
  other: "var(--fg-muted)",
};

export function formatDuration(
  min: number | null | undefined,
  hShort = "ч",
  mShort = "мин",
): string | null {
  if (min == null || min <= 0) return null;
  if (min < 60) return `${Math.round(min)} ${mShort}`;
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return m === 0 ? `${h}${hShort}` : `${h}${hShort} ${m}${mShort}`;
}

export function formatDistance(km: number | null | undefined): string | null {
  if (km == null || km <= 0) return null;
  return km < 10 ? km.toFixed(1) : Math.round(km).toString();
}

export function SessionHero({
  discipline,
  title,
  scheduledTime,
  timeOfDay,
  durationMinutes,
  distanceKm,
  hrTarget,
  paceTarget,
  intervals,
  onStart,
  onEdit,
  empty,
  className = "",
}: SessionHeroProps) {
  const { t } = useTranslation("dashboard");
  const sportKey = getSportKey(discipline);
  const dot = SPORT_DOT[sportKey] || SPORT_DOT.other;

  if (empty) {
    return (
      <div
        data-testid="session-hero-empty"
        className={`indigo-card p-5 ${className}`}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {t("today_label", { defaultValue: "Сегодня" })}
        </div>
        <div className="mt-2 text-base text-fg-secondary">
          {t("today_no_workout", { defaultValue: "Тренировки нет — отдыхаем" })}
        </div>
      </div>
    );
  }

  const timeLabel =
    timeOfDay === "morning"
      ? t("session.time_morning", { defaultValue: "Утро" })
      : timeOfDay === "midday"
      ? t("session.time_midday", { defaultValue: "День" })
      : timeOfDay === "evening"
      ? t("session.time_evening", { defaultValue: "Вечер" })
      : null;

  const headerBits: string[] = [t("session.heading", { defaultValue: "Сессия" }).toUpperCase()];
  if (scheduledTime) headerBits.push(scheduledTime);
  if (timeLabel) headerBits.push(timeLabel.toUpperCase());

  const duration = formatDuration(
    durationMinutes,
    t("common:hour_short", { defaultValue: "ч" }),
    t("common:min_short", { defaultValue: "мин" }),
  );
  const distance = formatDistance(distanceKm);

  const metrics: { label: string; value: string; unit?: string }[] = [];
  if (distance) {
    metrics.push({
      label: t("session.metric_volume", { defaultValue: "Объём" }),
      value: distance,
      unit: t("unit_km", { defaultValue: "км" }),
    });
  } else if (duration) {
    metrics.push({
      label: t("session.metric_duration", { defaultValue: "Длительность" }),
      value: duration,
    });
  }
  if (paceTarget) {
    metrics.push({
      label: t("session.metric_pace", { defaultValue: "Пейс" }),
      value: paceTarget,
      unit: "/" + t("unit_km", { defaultValue: "км" }),
    });
  }
  if (hrTarget) {
    metrics.push({
      label: t("session.metric_hr", { defaultValue: "Цель HR" }),
      value: hrTarget,
    });
  }

  return (
    <div
      data-testid="session-hero"
      data-sport={sportKey}
      className={`indigo-card p-5 flex flex-col gap-4 ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          data-testid="session-hero-dot"
          className="w-1.5 h-1.5 rounded-full inline-block"
          style={{ backgroundColor: dot }}
        />
        <span className="text-[10px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {headerBits.join(" · ")}
        </span>
      </div>

      {/* Canon section ScreenTodaySolo (main.dc.html:433) — workout headline
          at 30px ONE-display, bold, tracking-[-0.03em], line-height 1.05
          so the workout reads at-a-glance as the day's anchor. Was
          text-lg (18px). */}
      <div
        data-testid="session-hero-title"
        className="text-[28px] sm:text-[30px] font-bold text-fg-primary tracking-[-0.03em] leading-[1.05] font-display"
      >
        {title ?? t("default_workout_name", { defaultValue: "Тренировка" })}
        {duration && distance ? (
          <span className="text-fg-secondary font-normal text-[18px] ml-2"> · {duration}</span>
        ) : null}
      </div>

      {metrics.length > 0 && (
        <div
          data-testid="session-hero-metrics"
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}
        >
          {metrics.map((m) => (
            <div
              key={m.label}
              data-testid={`session-hero-metric-${m.label}`}
              className="rounded-md bg-bg-raised px-3 py-2.5"
            >
              <div className="text-[8.5px] uppercase tracking-[0.18em] text-fg-muted font-bold">
                {m.label}
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-[15px] font-bold text-fg-primary tabular-nums num-display">
                  {m.value}
                </span>
                {m.unit && (
                  <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">
                    {m.unit}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {intervals && intervals.length > 0 && (
        <div
          data-testid="session-hero-structure"
          className="rounded-md bg-bg-raised px-3.5 py-3"
        >
          <div className="text-[8.5px] uppercase tracking-[0.16em] text-fg-muted font-bold mb-2">
            {t("session.structure", { defaultValue: "Структура" })}
          </div>
          <div>
            {intervals.map((iv, i) => (
              <div
                key={`${iv.label}-${i}`}
                data-testid={`session-hero-interval-${i}`}
                className={`flex items-center gap-2.5 py-1.5 ${
                  i > 0 ? "border-t border-bg-border/60" : ""
                }`}
              >
                <span
                  className="w-[3px] h-6 rounded-sm inline-block"
                  style={{ backgroundColor: iv.zone ? ZONE_COLOR[iv.zone] : ZONE_COLOR[2] }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-fg-primary leading-tight">
                    {iv.label}
                  </div>
                  <div className="text-[9.5px] uppercase tracking-[0.1em] text-fg-muted font-semibold mt-0.5">
                    {iv.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(onStart || onEdit) && (
        <div className="flex gap-2">
          {onStart && (
            <button
              type="button"
              data-testid="session-hero-start"
              onClick={onStart}
              aria-label={t("session.cta_start_aria", { defaultValue: "Запустить тренировку" })}
              className="flex-1 px-0 py-3 rounded-md bg-accent text-white font-semibold text-sm hover:brightness-110 transition"
            >
              {t("session.cta_start", { defaultValue: "Запустить" })}
            </button>
          )}
          {onEdit && (
            <button
              type="button"
              data-testid="session-hero-edit"
              onClick={onEdit}
              aria-label={t("session.cta_edit_aria", { defaultValue: "Изменить тренировку" })}
              className="px-3.5 py-3 rounded-md border border-bg-border text-fg-secondary text-[13px] hover:bg-bg-raised transition"
            >
              {t("session.cta_edit", { defaultValue: "Изменить" })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
